import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Bell,
  BarChart3,
  Building2,
  CheckCircle2,
  CircleDashed,
  Clock3,
  FileText,
  FolderKanban,
  Globe2,
  LayoutDashboard,
  Layers3,
  LogOut,
  Plus,
  Palette,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  UserPlus,
  UserRound,
  Users,
  Workflow,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  companyUserRoleOptions,
  loadBusinessCenterCompanies,
  loadBusinessCenterCompanyUsers,
  mapCompanyRoleToPermissionRole,
  refreshBusinessCenterCompanies,
  refreshBusinessCenterCompanyUsers,
  portalAccessOptions,
  upsertBusinessCenterCompany,
  upsertBusinessCenterCompanyUsers,
  type CompanyUserRoleValue,
} from "./businessCenterCompanyUsersData";
import {
  calculateOnboardingCompletion,
  buildBusinessCenterOnboardingDashboard,
  buildOnboardingPortalUrl,
  createBusinessCenterOnboardingRecord,
  loadBusinessCenterOnboardingRecords,
  refreshBusinessCenterOnboardingRecords,
  onboardingCompanyTypeOptions,
  onboardingPortalStatusOptions,
  onboardingStatusOptions,
  onboardingWorkspaceOptions,
  upsertBusinessCenterOnboardingRecord,
} from "./businessCenterOnboardingData";
import {
  createBusinessCenterBrandingProfile,
  upsertBusinessCenterBrandingProfile,
} from "./businessCenterBrandingData";
import {
  loadBusinessCenterRoleAssignments,
  refreshBusinessCenterRoleAssignments,
  upsertBusinessCenterUserRoleAssignment,
} from "./businessCenterRolesData";
import type {
  BusinessCenterCompanyRecord,
  BusinessCenterOnboardingRecord,
  BusinessCenterOnboardingStatus,
  BusinessCenterUserRecord,
  ClientPortalWorkspaceKey,
} from "../../lib/types";
import type { LucideIcon } from "lucide-react";
import { logout } from "../../lib/api";
import { formatDate } from "../../lib/format";

type CompanyStatusValue = BusinessCenterCompanyRecord["status"];

interface OnboardingFormState {
  companyName: string;
  companySlug: string;
  companyType: BusinessCenterCompanyRecord["companyType"];
  companyStatus: CompanyStatusValue;
  workspaceAssignments: ClientPortalWorkspaceKey[];
  onboardingStatus: BusinessCenterOnboardingStatus;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryUserRole: CompanyUserRoleValue;
  primaryPortalAccessStatus: BusinessCenterUserRecord["portalAccessStatus"];
  secondaryContactName: string;
  secondaryContactEmail: string;
  secondaryUserRole: CompanyUserRoleValue | "";
  secondaryPortalAccessStatus: BusinessCenterUserRecord["portalAccessStatus"];
  portalStatus: BusinessCenterOnboardingRecord["portalStatus"];
  brandingCompanyName: string;
  brandingLogo: string;
  primaryColor: string;
  accentColor: string;
  contactPhone: string;
  contactEmail: string;
  notes: string;
  checklist: BusinessCenterOnboardingRecord["checklist"];
}

const businessCenterNavLinks = [
  { label: "Business Center OS Home", to: "/admin/business-center-os", icon: LayoutDashboard },
  { label: "CRM / Leads", to: "/admin/leads", icon: Sparkles },
  { label: "Client Center", to: "/admin/truevance-client-center", icon: UserRound },
  { label: "Project Center", to: "/admin/truevance-project-center", icon: Layers3 },
  { label: "Tasks Center", to: "/admin/truevance-tasks", icon: FolderKanban },
  { label: "Document Center", to: "/admin/truevance-document-center", icon: FileText },
  { label: "Universal User Management", to: "/admin/universal-user-management", icon: Users },
  { label: "Client Onboarding Center", to: "/admin/client-onboarding-center", icon: Building2 },
  { label: "Workspace Provisioning Center", to: "/admin/workspace-provisioning-center", icon: Globe2 },
  { label: "White Label Branding Center", to: "/admin/white-label-branding-center", icon: Palette },
  { label: "Automation Engine", to: "/admin/automation-engine", icon: Workflow },
  { label: "Roles & Permissions", to: "/admin/truevance-roles-center", icon: ShieldCheck },
  { label: "Reporting", to: "/admin/truevance-analytics-center", icon: BarChart3 },
  { label: "Billing", to: "/admin/payment-records", icon: FileText },
  { label: "CollabRecover AR Solutions", to: "/admin/ar-recovery", icon: BarChart3 },
  { label: "Settings", to: "/admin/truevance-settings-center", icon: Sparkles },
] as const;

const defaultChecklist: BusinessCenterOnboardingRecord["checklist"] = {
  companyCreated: true,
  usersAdded: false,
  rolesAssigned: false,
  portalActivated: false,
  brandingApplied: false,
  billingSetup: false,
  documentsUploaded: false,
};

const defaultForm: OnboardingFormState = {
  companyName: "",
  companySlug: "",
  companyType: "client_company",
  companyStatus: "active",
  workspaceAssignments: ["truevance_connect"],
  onboardingStatus: "Not Started",
  primaryContactName: "",
  primaryContactEmail: "",
  primaryUserRole: "company_admin",
  primaryPortalAccessStatus: "active",
  secondaryContactName: "",
  secondaryContactEmail: "",
  secondaryUserRole: "",
  secondaryPortalAccessStatus: "active",
  portalStatus: "draft",
  brandingCompanyName: "",
  brandingLogo: "",
  primaryColor: "#1F2937",
  accentColor: "#06B6D4",
  contactPhone: "",
  contactEmail: "",
  notes: "",
  checklist: defaultChecklist,
};

export function BusinessCenterOSOnboardingCenterPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<BusinessCenterCompanyRecord[]>([]);
  const [users, setUsers] = useState<BusinessCenterUserRecord[]>([]);
  const [records, setRecords] = useState<BusinessCenterOnboardingRecord[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState<OnboardingFormState>(defaultForm);

  useBusinessCenterOSBranding(
    "Client Onboarding Center",
    "Shared client onboarding workflow for company creation, user provisioning, portal setup, and workspace assignment.",
  );

  useEffect(() => {
    return loadWorkspace();
  }, []);

  const dashboard = useMemo(() => buildBusinessCenterOnboardingDashboard(records, companies, users), [companies, records, users]);

  const selectedRecord = useMemo(
    () => records.find((record) => record.id === selectedRecordId) || records[0] || null,
    [records, selectedRecordId],
  );

  const filteredRecords = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return records.filter((record) => {
      if (!normalized) return true;
      const completion = calculateOnboardingCompletion(record);
      const workspaceLabel = record.workspaceAssignments
        .map((workspace) => onboardingWorkspaceOptions.find((option) => option.key === workspace)?.label || workspace)
        .join(" ");
      return [
        record.companyName,
        record.primaryContactName,
        record.primaryContactEmail,
        record.status,
        record.portalStatus,
        record.notes,
        workspaceLabel,
        `${completion.percent}%`,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalized);
    });
  }, [query, records]);

  useEffect(() => {
    if (!selectedRecordId && records[0]) {
      setSelectedRecordId(records[0].id);
    }
    if (selectedRecordId && !records.some((record) => record.id === selectedRecordId) && records[0]) {
      setSelectedRecordId(records[0].id);
    }
  }, [records, selectedRecordId]);

  function loadWorkspace() {
    setLoading(true);
    setError("");
    let cancelled = false;
    Promise.all([
      refreshBusinessCenterCompanies().catch(() => loadBusinessCenterCompanies()),
      refreshBusinessCenterCompanyUsers().catch(() => loadBusinessCenterCompanyUsers()),
      refreshBusinessCenterRoleAssignments().catch(() => loadBusinessCenterRoleAssignments()),
      refreshBusinessCenterOnboardingRecords().catch(() => loadBusinessCenterOnboardingRecords()),
    ])
      .then(([nextCompanies, nextUsers, _nextRoleAssignments, nextRecords]) => {
        if (cancelled) return;
        setCompanies(nextCompanies);
        setUsers(nextUsers);
        setRecords(nextRecords);
        setSelectedRecordId((current) => (nextRecords.some((record) => record.id === current) ? current : nextRecords[0]?.id || ""));
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Unable to load onboarding workspace.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }

  function updateForm<K extends keyof OnboardingFormState>(key: K, value: OnboardingFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function toggleWorkspace(workspace: ClientPortalWorkspaceKey) {
    setForm((current) => {
      const exists = current.workspaceAssignments.includes(workspace);
      const workspaceAssignments = exists
        ? current.workspaceAssignments.filter((item) => item !== workspace)
        : [...current.workspaceAssignments, workspace];
      return { ...current, workspaceAssignments: workspaceAssignments.length > 0 ? workspaceAssignments : ["truevance_connect"] };
    });
  }

  function updateChecklist(key: keyof BusinessCenterOnboardingRecord["checklist"], value: boolean) {
    setForm((current) => ({ ...current, checklist: { ...current.checklist, [key]: value } }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const now = new Date().toISOString();
      const slugBase = buildSlug(form.companySlug || form.companyName || "untitled-company");
      const existingCompany = companies.find((company) =>
        company.id === `company-${slugBase}`
        || company.companySlug === slugBase
        || company.companyName.trim().toLowerCase() === form.companyName.trim().toLowerCase(),
      );
      const uniqueSlug = existingCompany?.companySlug || ensureUniqueCompanySlug(slugBase, companies);
      const parentCompany = companies.find((company) => company.companyType === "parent") || companies[0] || null;
      const companyId = existingCompany?.id || `company-${uniqueSlug}`;
      const nextCompany: BusinessCenterCompanyRecord = {
        id: companyId,
        parentCompanyId: existingCompany?.parentCompanyId || parentCompany?.id || null,
        companyName: form.companyName.trim() || "Untitled Company",
        companySlug: uniqueSlug,
        companyType: form.companyType,
        brand: form.workspaceAssignments[0] || "truevance_connect",
        status: form.companyStatus,
        createdAt: existingCompany?.createdAt || now,
        updatedAt: now,
      };

      upsertBusinessCenterCompany(nextCompany);

      const primaryName = form.primaryContactName.trim();
      const primaryEmail = normalizeEmail(form.primaryContactEmail);
      const secondaryName = form.secondaryContactName.trim();
      const secondaryEmail = normalizeEmail(form.secondaryContactEmail);
      const hasSecondaryUser = Boolean(secondaryName || secondaryEmail);

      if (!primaryName || !primaryEmail) {
        throw new Error("Primary contact name and email are required before creating onboarding users.");
      }
      if (hasSecondaryUser && (!secondaryName || !secondaryEmail)) {
        throw new Error("Secondary contact name and email are both required when adding a second user.");
      }
      if (hasSecondaryUser && primaryEmail === secondaryEmail) {
        throw new Error("Primary and secondary onboarding users must use different email addresses.");
      }
      if (
        form.primaryPortalAccessStatus !== "active"
        || (hasSecondaryUser && form.secondaryPortalAccessStatus !== "active")
      ) {
        throw new Error("Onboarding internal users require active portal access before they can be created.");
      }

      const requestedUsers = [
        createOnboardingUserRecord({
          companyId,
          parentCompanyId: nextCompany.parentCompanyId || null,
          fullName: primaryName,
          email: primaryEmail,
          role: normalizeCompanyUserRole(form.primaryUserRole),
          now,
        }),
        ...(hasSecondaryUser
          ? [
              createOnboardingUserRecord({
                companyId,
                parentCompanyId: nextCompany.parentCompanyId || null,
                fullName: secondaryName,
                email: secondaryEmail,
                role: normalizeCompanyUserRole(form.secondaryUserRole || "company_team_member"),
                now,
              }),
            ]
          : []),
      ];
      const nextUsers = await upsertBusinessCenterCompanyUsers(requestedUsers);
      const persistedUsers = requestedUsers.map((requestedUser) =>
        nextUsers.find((user) =>
          user.companyId === companyId && normalizeEmail(user.email) === normalizeEmail(requestedUser.email),
        ),
      );

      if (persistedUsers.some((user) => !user)) {
        throw new Error("Onboarding users could not be confirmed after saving. No user success was recorded.");
      }

      const confirmedUsers = persistedUsers as BusinessCenterUserRecord[];
      const primaryUser = confirmedUsers[0];
      const additionalUsers = confirmedUsers.slice(1);
      setUsers(nextUsers);

      confirmedUsers.forEach((user, index) => {
        upsertBusinessCenterUserRoleAssignment({
          id: user.id,
          userName: user.fullName,
          userEmail: user.email,
          role: mapCompanyRoleToPermissionRole(user.role),
          recordType: "internal",
          notes: `${index === 0 ? "Primary onboarding contact" : "Secondary onboarding user"} for ${nextCompany.companyName}.`,
          createdAt: user.createdAt,
          updatedAt: now,
        });
      });

      const onboardingRecord = createBusinessCenterOnboardingRecord({
        id: `onboarding-${companyId}`,
        companyId,
        companyName: nextCompany.companyName,
        companySlug: nextCompany.companySlug,
        companyType: nextCompany.companyType,
        workspaceAssignments: form.workspaceAssignments,
        status: form.onboardingStatus,
        primaryContactName: primaryUser.fullName,
        primaryContactEmail: primaryUser.email,
        createdUserCount: confirmedUsers.length,
        assignedRoleKeys: Array.from(
          new Set([
            mapCompanyRoleToPermissionRole(primaryUser.role),
            ...additionalUsers.map((user) => mapCompanyRoleToPermissionRole(user.role)),
          ]),
        ),
        portalStatus: form.portalStatus,
        portalUrl: buildOnboardingPortalUrl(nextCompany.companySlug, form.workspaceAssignments),
        branding: {
          companyName: form.brandingCompanyName.trim() || nextCompany.companyName,
          logo: form.brandingLogo.trim(),
          primaryColor: form.primaryColor,
          accentColor: form.accentColor,
          contactPhone: form.contactPhone.trim(),
          contactEmail: form.contactEmail.trim() || primaryUser.email,
        },
        checklist: form.checklist,
        notes: form.notes.trim(),
        createdAt: now,
        updatedAt: now,
      });

      upsertBusinessCenterOnboardingRecord(onboardingRecord);
      upsertBusinessCenterBrandingProfile(
        createBusinessCenterBrandingProfile({
          companyId,
          companyName: nextCompany.companyName,
          companySlug: nextCompany.companySlug,
          publicDisplayName: form.brandingCompanyName.trim() || nextCompany.companyName,
          logoUrl: form.brandingLogo.trim(),
          faviconUrl: `${nextCompany.companySlug}-favicon.ico`,
          primaryColor: form.primaryColor,
          secondaryColor: form.accentColor,
          accentColor: form.accentColor,
          contactEmail: form.contactEmail.trim() || primaryUser.email,
          supportEmail: form.contactEmail.trim() || primaryUser.email,
          phoneNumber: form.contactPhone.trim(),
          website: `https://${nextCompany.companySlug}.com`,
          companyDescription: form.notes.trim() || `${nextCompany.companyName} branded portal experience.`,
          portal: {
            name: `${form.brandingCompanyName.trim() || nextCompany.companyName} Portal`,
            subtitle: `${nextCompany.companyName} client experience`,
            logoUrl: form.brandingLogo.trim(),
            primaryColor: form.primaryColor,
            secondaryColor: form.accentColor,
            accentColor: form.accentColor,
            welcomeMessage: form.notes.trim() || "Welcome to your shared Business Center portal.",
          },
          documents: {
            logoUrl: form.brandingLogo.trim(),
            footerText: `${nextCompany.companyName} documents`,
            signatureBlock: `${nextCompany.companyName}\nBusiness Center OS`,
            companyAddress: "",
            companyContactInformation: form.contactEmail.trim() || primaryUser.email,
          },
          email: {
            logoUrl: form.brandingLogo.trim(),
            headerColor: form.primaryColor,
            footerColor: form.accentColor,
            signature: `${nextCompany.companyName} Team`,
            companyFooter: `${nextCompany.companyName} powered by Business Center OS.`,
          },
          domains: {
            primaryDomain: `${nextCompany.companySlug}.com`,
            portalDomain: `portal.${nextCompany.companySlug}.com`,
            customSubdomain: `${nextCompany.companySlug}.business`,
            loginUrl: `https://${nextCompany.companySlug}.com/login`,
          },
          toggles: {
            showPoweredByBusinessCenterOS: true,
            showWorkspaceName: true,
            showCompanyName: true,
          },
          sourceOnboardingId: onboardingRecord.id,
        }),
      );
      setMessage(`Created onboarding workspace for ${nextCompany.companyName}.`);
      resetForm();
      loadWorkspace();
      setSelectedRecordId(onboardingRecord.id);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to create onboarding record.");
    } finally {
      setSaving(false);
    }
  }

  function resetForm() {
    setForm({
      ...defaultForm,
      workspaceAssignments: form.workspaceAssignments.length > 0 ? form.workspaceAssignments : ["truevance_connect"],
    });
  }

  function promoteRecord(recordId: string, nextStatus: BusinessCenterOnboardingStatus) {
    const record = records.find((item) => item.id === recordId);
    if (!record) return;

    const nextRecord: BusinessCenterOnboardingRecord = {
      ...record,
      status: nextStatus,
      portalStatus: nextStatus === "Complete" ? "active" : record.portalStatus,
      checklist:
        nextStatus === "Complete"
          ? {
              companyCreated: true,
              usersAdded: true,
              rolesAssigned: true,
              portalActivated: true,
              brandingApplied: true,
              billingSetup: true,
              documentsUploaded: true,
            }
          : record.checklist,
      updatedAt: new Date().toISOString(),
    };

    upsertBusinessCenterOnboardingRecord(nextRecord);
    loadWorkspace();
    setSelectedRecordId(recordId);
  }

  function toggleRecordChecklist(recordId: string, key: keyof BusinessCenterOnboardingRecord["checklist"]) {
    const record = records.find((item) => item.id === recordId);
    if (!record) return;
    const nextChecklist = { ...record.checklist, [key]: !record.checklist[key] };
    const completion = calculateOnboardingCompletion({ ...record, checklist: nextChecklist });
    const nextRecord: BusinessCenterOnboardingRecord = {
      ...record,
      checklist: nextChecklist,
      status: completion.percent === 100 ? "Complete" : completion.completed === 0 ? "Not Started" : completion.completed <= 3 ? "Pending" : "In Progress",
      portalStatus: nextChecklist.portalActivated ? "active" : record.portalStatus === "active" ? "pending" : record.portalStatus,
      updatedAt: new Date().toISOString(),
    };
    upsertBusinessCenterOnboardingRecord(nextRecord);
    loadWorkspace();
    setSelectedRecordId(recordId);
  }

  function handleLogout() {
    logout().finally(() => navigate("/login"));
  }

  return (
    <main className="business-center-os-shell min-h-screen text-[#F8FAFC]">
      <BusinessCenterOSHeader onLogout={handleLogout} />

      <section className="business-center-os-hero border-b border-[rgba(34,197,94,.16)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.1fr_.9fr] lg:items-end">
          <div className="grid gap-4">
            <div className="business-center-os-pill inline-flex w-fit items-center gap-2 rounded-full px-3 py-2 text-xs font-black uppercase tracking-[0.22em]">
              <Building2 className="size-4" aria-hidden="true" />
              Business Center OS
            </div>
            <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Client Onboarding Center</h1>
            <p className="max-w-3xl text-base font-semibold leading-8 text-[#E2E8F0]">
              One shared onboarding engine for company creation, user provisioning, portal activation, branding, and
              workspace assignment across Kreatethedate, Truevance Connect, AR Recovery, and future white-label clients.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/admin/business-center-os"
                className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold"
              >
                <ArrowLeft className="size-4" aria-hidden="true" />
                Back to Business Center OS
              </Link>
              <Link
                to="/admin/client-onboarding-center"
                className="business-center-os-button focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold"
              >
                <ArrowRight className="size-4" aria-hidden="true" />
                Shared onboarding shell
              </Link>
            </div>
          </div>

          <div className="business-center-os-card grid gap-4 p-5">
            <div className="grid grid-cols-2 gap-3 text-sm font-semibold">
              <StatChip label="Total companies" value={dashboard.totalCompanies} />
              <StatChip label="Active onboardings" value={dashboard.activeOnboardings} />
              <StatChip label="Pending tasks" value={dashboard.pendingTasks} />
              <StatChip label="Completed" value={dashboard.completedOnboardings} />
            </div>
            <div className="rounded-3xl border border-[rgba(34,197,94,.18)] bg-[rgba(255,255,255,.03)] p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">Completion</p>
                  <p className="mt-1 text-3xl font-black">{dashboard.averageCompletion}%</p>
                </div>
                <BadgeCheck className="size-8 text-[#86EFAC]" aria-hidden="true" />
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-gradient-to-r from-[#22C55E] via-[#14B8A6] to-[#06B6D4]" style={{ width: `${dashboard.averageCompletion}%` }} />
              </div>
              <p className="mt-3 text-sm leading-6 text-[#C9D6DF]">
                Shared workflow readiness across all company onboarding records. Each step can later drive email, billing, and portal automations.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[rgba(34,197,94,.12)] px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 overflow-x-auto">
          {businessCenterNavLinks.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `focus-ring inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 text-sm font-bold transition ${
                    isActive
                      ? "border-[rgba(34,197,94,.55)] bg-[rgba(34,197,94,.16)] text-[#F5F7FA] shadow-[0_16px_32px_rgba(34,197,94,.16)]"
                      : "border-[rgba(34,197,94,.18)] bg-[rgba(255,255,255,.03)] text-[#E8EEF4] hover:border-[rgba(34,197,94,.42)] hover:bg-[rgba(255,255,255,.06)]"
                  }`
                }
              >
                <Icon className="size-4" aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </section>

      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Total companies" value={dashboard.totalCompanies} note="Parent-free client companies in the workspace." />
          <MetricCard label="Average completion" value={`${dashboard.averageCompletion}%`} note="Average progress across onboarding records." />
          <MetricCard label="Portal-ready" value={dashboard.portalStatusCounts.active} note="Onboardings with active portal status." />
          <MetricCard label="Pending steps" value={dashboard.pendingTasks} note="Checklist items that still need attention." />
        </div>
      </section>

      <section className="px-4 pb-8 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[1.1fr_.9fr]">
          <div className="grid gap-6">
            <article className="business-center-os-card grid gap-5 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">Workflow intake</p>
                  <h2 className="mt-2 text-2xl font-black text-white">Create a new onboarding record</h2>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-[#C9D6DF]">
                    Provision the company, primary contact, optional secondary user, portal access, branding, and checklist in one shared workflow.
                  </p>
                </div>
                <div className="rounded-2xl border border-[rgba(34,197,94,.16)] bg-[rgba(255,255,255,.03)] px-4 py-3 text-sm font-semibold text-[#E2E8F0]">
                  Workspace preview: {form.workspaceAssignments.map((workspace) => onboardingWorkspaceOptions.find((item) => item.key === workspace)?.label || workspace).join(", ")}
                </div>
              </div>

              <form className="grid gap-5" onSubmit={handleSubmit}>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Company name">
                    <input
                      value={form.companyName}
                      onChange={(event) => updateForm("companyName", event.target.value)}
                      className="business-center-os-input"
                      placeholder="Company or client name"
                    />
                  </Field>
                  <Field label="Company slug">
                    <input
                      value={form.companySlug}
                      onChange={(event) => updateForm("companySlug", event.target.value)}
                      className="business-center-os-input"
                      placeholder="company-slug"
                    />
                  </Field>
                  <Field label="Company type">
                    <select
                      value={form.companyType}
                      onChange={(event) => updateForm("companyType", event.target.value as OnboardingFormState["companyType"])}
                      className="business-center-os-input"
                    >
                      {onboardingCompanyTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Company status">
                    <select
                      value={form.companyStatus}
                      onChange={(event) => updateForm("companyStatus", event.target.value as CompanyStatusValue)}
                      className="business-center-os-input"
                    >
                      {["active", "inactive", "archived"].map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Workspace assignment" helper="Choose one or more shared Business Center workspaces.">
                    <div className="grid gap-3">
                      {onboardingWorkspaceOptions.map((workspace) => {
                        const checked = form.workspaceAssignments.includes(workspace.key);
                        return (
                          <label
                            key={workspace.key}
                            className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
                              checked
                                ? "border-[rgba(34,197,94,.55)] bg-[rgba(34,197,94,.08)]"
                                : "border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.02)] hover:border-[rgba(34,197,94,.3)]"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleWorkspace(workspace.key)}
                              className="mt-1 size-4 rounded border-white/20 bg-transparent text-emerald-400"
                            />
                            <div className="grid gap-1">
                              <span className="text-sm font-bold text-white">{workspace.label}</span>
                              <span className="text-xs leading-5 text-[#C9D6DF]">{workspace.description}</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </Field>

                  <Field label="Onboarding status">
                    <select
                      value={form.onboardingStatus}
                      onChange={(event) => updateForm("onboardingStatus", event.target.value as BusinessCenterOnboardingStatus)}
                      className="business-center-os-input"
                    >
                      {onboardingStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Primary contact name">
                    <input
                      value={form.primaryContactName}
                      onChange={(event) => updateForm("primaryContactName", event.target.value)}
                      className="business-center-os-input"
                      placeholder="Primary contact"
                    />
                  </Field>
                  <Field label="Primary contact email">
                    <input
                      value={form.primaryContactEmail}
                      onChange={(event) => updateForm("primaryContactEmail", event.target.value)}
                      className="business-center-os-input"
                      placeholder="contact@company.com"
                    />
                  </Field>
                  <Field label="Primary user role">
                    <select
                      value={form.primaryUserRole}
                      onChange={(event) => updateForm("primaryUserRole", event.target.value as CompanyUserRoleValue)}
                      className="business-center-os-input"
                    >
                      {companyUserRoleOptions.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Primary portal access">
                    <select
                      value={form.primaryPortalAccessStatus}
                      onChange={(event) => updateForm("primaryPortalAccessStatus", event.target.value as BusinessCenterUserRecord["portalAccessStatus"])}
                      className="business-center-os-input"
                    >
                      {portalAccessOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Secondary user name" helper="Optional additional user for the company.">
                    <input
                      value={form.secondaryContactName}
                      onChange={(event) => updateForm("secondaryContactName", event.target.value)}
                      className="business-center-os-input"
                      placeholder="Optional second user"
                    />
                  </Field>
                  <Field label="Secondary user email">
                    <input
                      value={form.secondaryContactEmail}
                      onChange={(event) => updateForm("secondaryContactEmail", event.target.value)}
                      className="business-center-os-input"
                      placeholder="optional@company.com"
                    />
                  </Field>
                  <Field label="Secondary user role">
                    <select
                      value={form.secondaryUserRole}
                      onChange={(event) => updateForm("secondaryUserRole", event.target.value as CompanyUserRoleValue | "")}
                      className="business-center-os-input"
                    >
                      <option value="">No secondary user</option>
                      {companyUserRoleOptions.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Secondary portal access">
                    <select
                      value={form.secondaryPortalAccessStatus}
                      onChange={(event) => updateForm("secondaryPortalAccessStatus", event.target.value as BusinessCenterUserRecord["portalAccessStatus"])}
                      className="business-center-os-input"
                    >
                      {portalAccessOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="grid gap-4 lg:grid-cols-[1.1fr_.9fr]">
                  <Field label="Portal status">
                    <select
                      value={form.portalStatus}
                      onChange={(event) => updateForm("portalStatus", event.target.value as OnboardingFormState["portalStatus"])}
                      className="business-center-os-input"
                    >
                      {onboardingPortalStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Portal URL preview">
                    <input
                      readOnly
                      value={buildOnboardingPortalUrl(form.companySlug || form.companyName || "company", form.workspaceAssignments)}
                      className="business-center-os-input opacity-80"
                    />
                  </Field>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Brand company name">
                    <input
                      value={form.brandingCompanyName}
                      onChange={(event) => updateForm("brandingCompanyName", event.target.value)}
                      className="business-center-os-input"
                      placeholder="Brand/company display name"
                    />
                  </Field>
                  <Field label="Brand logo placeholder">
                    <input
                      value={form.brandingLogo}
                      onChange={(event) => updateForm("brandingLogo", event.target.value)}
                      className="business-center-os-input"
                      placeholder="logo.svg or brand asset URL"
                    />
                  </Field>
                  <Field label="Primary color">
                    <input
                      value={form.primaryColor}
                      onChange={(event) => updateForm("primaryColor", event.target.value)}
                      className="business-center-os-input"
                      placeholder="#1F2937"
                    />
                  </Field>
                  <Field label="Accent color">
                    <input
                      value={form.accentColor}
                      onChange={(event) => updateForm("accentColor", event.target.value)}
                      className="business-center-os-input"
                      placeholder="#06B6D4"
                    />
                  </Field>
                  <Field label="Contact phone">
                    <input
                      value={form.contactPhone}
                      onChange={(event) => updateForm("contactPhone", event.target.value)}
                      className="business-center-os-input"
                      placeholder="(555) 555-5555"
                    />
                  </Field>
                  <Field label="Contact email">
                    <input
                      value={form.contactEmail}
                      onChange={(event) => updateForm("contactEmail", event.target.value)}
                      className="business-center-os-input"
                      placeholder="hello@company.com"
                    />
                  </Field>
                </div>

                <div className="rounded-3xl border border-[rgba(34,197,94,.12)] bg-[rgba(255,255,255,.02)] p-4">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {(
                      Object.entries(form.checklist) as Array<
                        [keyof BusinessCenterOnboardingRecord["checklist"], boolean]
                      >
                    ).map(([key, value]) => (
                      <label
                        key={key}
                        className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
                          value
                            ? "border-[rgba(34,197,94,.45)] bg-[rgba(34,197,94,.08)]"
                            : "border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.02)] hover:border-[rgba(34,197,94,.3)]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(event) => updateChecklist(key as keyof BusinessCenterOnboardingRecord["checklist"], event.target.checked)}
                          className="mt-1 size-4 rounded border-white/20 bg-transparent text-emerald-400"
                        />
                        <div className="grid gap-1">
                          <span className="text-sm font-bold text-white">{formatChecklistLabel(key)}</span>
                          <span className="text-xs leading-5 text-[#C9D6DF]">
                            {formatChecklistHint(key)}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <Field label="Notes">
                  <textarea
                    value={form.notes}
                    onChange={(event) => updateForm("notes", event.target.value)}
                    className="business-center-os-input min-h-32"
                    placeholder="Capture onboarding context, special rollout notes, or automation ideas."
                  />
                </Field>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-[#C9D6DF]">
                    Estimated completion: {estimateChecklistCompletion(form.checklist)}% with {companyUserRoleOptions.find((option) => option.key === form.primaryUserRole)?.label || form.primaryUserRole} as the primary role.
                  </div>
                  <button
                    type="submit"
                    disabled={saving}
                    className="business-center-os-button focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-5 text-sm font-extrabold disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    {saving ? "Creating..." : "Create onboarding"}
                  </button>
                </div>
              </form>
            </article>

            <article className="business-center-os-card grid gap-4 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">Onboarding queue</p>
                  <h2 className="mt-2 text-2xl font-black text-white">Shared onboarding records</h2>
                </div>
                <div className="relative min-w-72 flex-1 max-w-xl">
                  <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-white/45" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    className="business-center-os-input pl-11"
                    placeholder="Search by company, contact, workspace, status..."
                  />
                </div>
              </div>

              <div className="grid gap-3">
                {filteredRecords.map((record) => {
                  const completion = calculateOnboardingCompletion(record);
                  const workspaceLabels = record.workspaceAssignments
                    .map((workspace) => onboardingWorkspaceOptions.find((option) => option.key === workspace)?.label || workspace)
                    .join(" • ");
                  return (
                    <button
                      key={record.id}
                      type="button"
                      onClick={() => setSelectedRecordId(record.id)}
                      className={`grid gap-3 rounded-3xl border p-4 text-left transition ${
                        selectedRecord?.id === record.id
                          ? "border-[rgba(34,197,94,.55)] bg-[rgba(34,197,94,.08)]"
                          : "border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.02)] hover:border-[rgba(34,197,94,.35)] hover:bg-[rgba(255,255,255,.04)]"
                      }`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="grid gap-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-lg font-black text-white">{record.companyName}</h3>
                            <span className="rounded-full border border-[rgba(34,197,94,.2)] px-2.5 py-1 text-[0.7rem] font-black uppercase tracking-[0.16em] text-[#86EFAC]">
                              {record.status}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-[#C9D6DF]">{record.primaryContactName} • {record.primaryContactEmail}</p>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8AA0B8]">{workspaceLabels}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">Progress</p>
                          <p className="mt-1 text-2xl font-black text-white">{completion.percent}%</p>
                        </div>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-white/10">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#22C55E] via-[#14B8A6] to-[#06B6D4]" style={{ width: `${completion.percent}%` }} />
                      </div>
                      <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[0.7rem] font-bold text-[#D9E6F2]">
                            Portal: {record.portalStatus}
                          </span>
                          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[0.7rem] font-bold text-[#D9E6F2]">
                            Roles: {record.assignedRoleKeys.join(", ") || "viewer"}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <RecordActionButton label="In Progress" onClick={() => promoteRecord(record.id, "In Progress")} />
                          <RecordActionButton label="Pending" onClick={() => promoteRecord(record.id, "Pending")} />
                          <RecordActionButton label="Complete" onClick={() => promoteRecord(record.id, "Complete")} />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </article>
          </div>

          <div className="grid gap-6">
            <article className="business-center-os-card grid gap-4 p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">Record detail</p>
                  <h2 className="mt-2 text-2xl font-black text-white">Selected onboarding record</h2>
                </div>
                <span className="rounded-full border border-[rgba(34,197,94,.2)] px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-[#86EFAC]">
                  {selectedRecord ? selectedRecord.status : "No record"}
                </span>
              </div>

              {selectedRecord ? (
                <div className="grid gap-4">
                  <div className="rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="grid gap-1">
                        <h3 className="text-xl font-black text-white">{selectedRecord.companyName}</h3>
                        <p className="text-sm font-semibold text-[#C9D6DF]">
                          {selectedRecord.primaryContactName} • {selectedRecord.primaryContactEmail}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8AA0B8]">
                          {selectedRecord.workspaceAssignments
                            .map((workspace) => onboardingWorkspaceOptions.find((item) => item.key === workspace)?.label || workspace)
                            .join(" • ")}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">Portal</p>
                        <p className="mt-1 text-sm font-bold text-white">{selectedRecord.portalStatus}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link
                        to={selectedRecord.portalUrl}
                        className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center gap-2 rounded-full px-4 text-xs font-extrabold"
                      >
                        <Globe2 className="size-4" aria-hidden="true" />
                        Open portal preview
                      </Link>
                      <button
                        type="button"
                        onClick={() => promoteRecord(selectedRecord.id, "Complete")}
                        className="business-center-os-button focus-ring inline-flex min-h-10 items-center gap-2 rounded-full px-4 text-xs font-extrabold"
                      >
                        <CheckCircle2 className="size-4" aria-hidden="true" />
                        Mark complete
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    {(
                      Object.entries(selectedRecord.checklist) as Array<
                        [keyof BusinessCenterOnboardingRecord["checklist"], boolean]
                      >
                    ).map(([key, value]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleRecordChecklist(selectedRecord.id, key as keyof BusinessCenterOnboardingRecord["checklist"])}
                        className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                          value
                            ? "border-[rgba(34,197,94,.45)] bg-[rgba(34,197,94,.08)]"
                            : "border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.02)] hover:border-[rgba(34,197,94,.3)]"
                        }`}
                      >
                        <span className={`mt-1 inline-flex size-5 items-center justify-center rounded-full ${value ? "bg-[#22C55E]/20 text-[#86EFAC]" : "bg-white/10 text-white/60"}`}>
                          {value ? <CheckCircle2 className="size-3.5" aria-hidden="true" /> : <CircleDashed className="size-3.5" aria-hidden="true" />}
                        </span>
                        <div className="grid gap-1">
                          <span className="text-sm font-bold text-white">{formatChecklistLabel(key)}</span>
                          <span className="text-xs leading-5 text-[#C9D6DF]">{formatChecklistHint(key)}</span>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="grid gap-3 rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                    <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">Automation readiness</p>
                    <ul className="grid gap-2 text-sm font-semibold text-[#D9E6F2]">
                      <li className="flex items-start gap-2"><Bell className="mt-0.5 size-4 text-[#86EFAC]" aria-hidden="true" />Email automation hooks can trigger from checklist completion and portal status updates.</li>
                      <li className="flex items-start gap-2"><Send className="mt-0.5 size-4 text-[#86EFAC]" aria-hidden="true" />Portal activation can later spawn invite, reminder, and welcome workflows.</li>
                      <li className="flex items-start gap-2"><Sparkles className="mt-0.5 size-4 text-[#86EFAC]" aria-hidden="true" />Billing, document uploads, and project assignment are already modeled for future automation.</li>
                    </ul>
                  </div>

                  <div className="grid gap-3 rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4 text-sm font-semibold text-[#C9D6DF]">
                    <div className="flex items-center justify-between gap-3">
                      <span>Created</span>
                      <span className="text-white">{formatDate(selectedRecord.createdAt)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>Updated</span>
                      <span className="text-white">{formatDate(selectedRecord.updatedAt)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>Users provisioned</span>
                      <span className="text-white">{selectedRecord.createdUserCount}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>Completion</span>
                      <span className="text-white">{calculateOnboardingCompletion(selectedRecord).percent}%</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-3xl border border-dashed border-white/15 bg-white/5 p-8 text-sm font-semibold text-[#C9D6DF]">
                  No onboarding record is selected yet. Create a new company onboarding or choose an existing record from the queue.
                </div>
              )}
            </article>

            <article className="business-center-os-card grid gap-4 p-5">
              <div>
                <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">Workspace scope</p>
                <h2 className="mt-2 text-2xl font-black text-white">Current model coverage</h2>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                {onboardingWorkspaceOptions.map((workspace) => (
                  <div key={workspace.key} className="rounded-2xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                    <p className="text-sm font-black text-white">{workspace.label}</p>
                    <p className="mt-1 text-xs leading-5 text-[#C9D6DF]">{workspace.description}</p>
                    <p className="mt-3 text-2xl font-black text-[#86EFAC]">{dashboard.workspaceCounts[workspace.key]}</p>
                  </div>
                ))}
              </div>

              <div className="grid gap-3 rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4 text-sm font-semibold text-[#D9E6F2]">
                <div className="flex items-center justify-between gap-3">
                  <span>Active companies loaded</span>
                  <span className="text-white">{companies.filter((company) => company.companyType !== "parent").length}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Provisioned users loaded</span>
                  <span className="text-white">{users.length}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Role assignments</span>
                  <span className="text-white">{loadBusinessCenterRoleAssignments().length}</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="px-4 pb-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-3">
          <WorkflowCard
            icon={UserPlus}
            title="User provisioning"
            description="Create primary and secondary company users with role assignment mappings that can feed future access enforcement."
          />
          <WorkflowCard
            icon={Palette}
            title="Brand assignment"
            description="Capture brand colors, logo placeholders, and contact details once so every workspace can inherit a consistent identity."
          />
          <WorkflowCard
            icon={Clock3}
            title="Automation readiness"
            description="Checklist transitions are already modeled so email, billing, and portal automations can be attached later without redesigning the workflow."
          />
        </div>
      </section>

      {(error || message) && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md rounded-3xl border border-[rgba(34,197,94,.2)] bg-[#08111A]/95 p-4 shadow-[0_24px_60px_rgba(0,0,0,.35)] backdrop-blur-xl">
          <p className={`text-sm font-semibold ${error ? "text-[#FCA5A5]" : "text-[#86EFAC]"}`}>{error || message}</p>
        </div>
      )}

      {loading ? (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-[#07111A]/60 backdrop-blur-sm">
          <div className="rounded-3xl border border-white/10 bg-[#0B1622] px-5 py-4 text-sm font-semibold text-[#D9E6F2] shadow-[0_24px_60px_rgba(0,0,0,.35)]">
            Loading onboarding workspace...
          </div>
        </div>
      ) : null}
    </main>
  );
}

function StatChip({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-2xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] px-4 py-3">
      <p className="text-[0.65rem] font-black uppercase tracking-[0.2em] text-[#8AA0B8]">{label}</p>
      <p className="mt-1 text-2xl font-black text-white">{value}</p>
    </div>
  );
}

function MetricCard({ label, value, note }: { label: string; value: string | number; note: string }) {
  return (
    <div className="business-center-os-card grid gap-2 rounded-3xl p-5">
      <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#86EFAC]">{label}</p>
      <p className="text-3xl font-black text-white">{value}</p>
      <p className="text-sm font-semibold leading-6 text-[#C9D6DF]">{note}</p>
    </div>
  );
}

function WorkflowCard({ icon: Icon, title, description }: { icon: LucideIcon; title: string; description: string }) {
  return (
    <article className="business-center-os-card grid gap-3 rounded-3xl p-5">
      <div className="flex items-center gap-3">
        <span className="inline-flex size-11 items-center justify-center rounded-2xl border border-[rgba(34,197,94,.16)] bg-[rgba(34,197,94,.08)] text-[#86EFAC]">
          <Icon className="size-5" aria-hidden="true" />
        </span>
        <h3 className="text-lg font-black text-white">{title}</h3>
      </div>
      <p className="text-sm leading-6 font-semibold text-[#C9D6DF]">{description}</p>
    </article>
  );
}

function RecordActionButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className="rounded-full border border-[rgba(255,255,255,.12)] bg-white/5 px-3 py-1.5 text-[0.7rem] font-black uppercase tracking-[0.16em] text-[#D9E6F2] transition hover:border-[rgba(34,197,94,.35)] hover:bg-[rgba(34,197,94,.08)]"
    >
      {label}
    </button>
  );
}

function BusinessCenterOSHeader({ onLogout }: { onLogout: () => void }) {
  return (
    <header className="sticky top-0 z-30 border-b border-[rgba(34,197,94,.14)] bg-[#08111A]/95 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/admin/business-center-os"
            className="inline-flex items-center gap-2 rounded-full border border-[rgba(34,197,94,.18)] bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#E2E8F0] transition hover:border-[rgba(34,197,94,.4)] hover:bg-white/10"
          >
            <Building2 className="size-4" aria-hidden="true" />
            Business Center OS
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-full border border-[rgba(34,197,94,.18)] bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#E2E8F0] transition hover:border-[rgba(34,197,94,.4)] hover:bg-white/10"
          >
            <LogOut className="size-4" aria-hidden="true" />
            Log out
          </button>
        </div>
        <nav className="flex gap-2 overflow-x-auto pb-1">
          {businessCenterNavLinks.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `focus-ring inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 text-sm font-bold transition ${
                    isActive
                      ? "border-[rgba(34,197,94,.55)] bg-[rgba(34,197,94,.16)] text-[#F5F7FA] shadow-[0_16px_32px_rgba(34,197,94,.16)]"
                      : "border-[rgba(34,197,94,.18)] bg-[rgba(255,255,255,.03)] text-[#E8EEF4] hover:border-[rgba(34,197,94,.42)] hover:bg-[rgba(255,255,255,.06)]"
                  }`
                }
              >
                <Icon className="size-4" aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </header>
  );
}

function Field({ label, helper, children }: { label: string; helper?: string; children: ReactNode }) {
  return (
    <label className="grid gap-2">
      <div className="flex items-end justify-between gap-3">
        <span className="text-xs font-black uppercase tracking-[0.2em] text-[#8AA0B8]">{label}</span>
        {helper ? <span className="text-[0.72rem] font-semibold text-[#74879D]">{helper}</span> : null}
      </div>
      {children}
    </label>
  );
}

function useBusinessCenterOSBranding(title: string, description: string) {
  useEffect(() => {
    document.title = `${title} | Business Center OS`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#0F1D2B");

    const faviconHref = buildBusinessCenterFavicon();
    const icons = ['link[rel="icon"]', 'link[rel="shortcut icon"]']
      .map((selector) => document.querySelector<HTMLLinkElement>(selector))
      .filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIcons = icons.map((link) => link.getAttribute("href"));
    icons.forEach((link) => {
      link.setAttribute("href", faviconHref);
      link.setAttribute("type", "image/svg+xml");
    });

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
      icons.forEach((link, index) => {
        const previousHref = previousIcons[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

function buildBusinessCenterFavicon() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="Business Center OS">
      <defs>
        <linearGradient id="g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#22C55E"/>
          <stop offset=".52" stop-color="#14B8A6"/>
          <stop offset="1" stop-color="#06B6D4"/>
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#0F1D2B"/>
      <path d="M16 16h18c9 0 16 6.5 16 14.5S43 45 34 45H16z" fill="url(#g)"/>
      <path d="M33 16h15c-4 3.3-6.8 7.6-6.8 13.6 0 6.2 3.2 10.6 7.8 15.4H33c-8.8 0-16-6.4-16-14.5S24.2 16 33 16Z" fill="#F8FAFC" opacity=".1"/>
      <text x="32" y="39" text-anchor="middle" font-size="16" font-family="Arial, Helvetica, sans-serif" font-weight="800" fill="#F8FAFC">BC</text>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function estimateChecklistCompletion(checklist: BusinessCenterOnboardingRecord["checklist"]) {
  return Math.round((Object.values(checklist).filter(Boolean).length / Object.keys(checklist).length) * 100);
}

function formatChecklistLabel(key: keyof BusinessCenterOnboardingRecord["checklist"]) {
  return {
    companyCreated: "Company created",
    usersAdded: "Users added",
    rolesAssigned: "Roles assigned",
    portalActivated: "Portal activated",
    brandingApplied: "Branding applied",
    billingSetup: "Billing setup",
    documentsUploaded: "Documents uploaded",
  }[key];
}

function formatChecklistHint(key: keyof BusinessCenterOnboardingRecord["checklist"]) {
  return {
    companyCreated: "Company record and workspace account exist.",
    usersAdded: "Primary and optional secondary users are provisioned.",
    rolesAssigned: "Permission assignments are mapped for the company.",
    portalActivated: "Client portal access can be issued and tracked.",
    brandingApplied: "Logo, colors, and contact details are captured.",
    billingSetup: "Billing foundations are ready for later automation.",
    documentsUploaded: "Documents can be attached once storage is enabled.",
  }[key];
}

function buildSlug(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-") || "untitled-company";
}

function createOnboardingUserRecord({
  companyId,
  parentCompanyId,
  fullName,
  email,
  role,
  now,
}: {
  companyId: string;
  parentCompanyId: string | null;
  fullName: string;
  email: string;
  role: CompanyUserRoleValue;
  now: string;
}): BusinessCenterUserRecord {
  const { firstName, lastName } = splitFullName(fullName);
  return {
    id: crypto.randomUUID(),
    companyId,
    parentCompanyId,
    firstName,
    lastName,
    displayName: fullName,
    fullName,
    email,
    jobTitle: getOnboardingJobTitle(role),
    department: "Recovery Operations",
    phone: "",
    team: "AR Recovery",
    notificationPreference: "both",
    role,
    status: "active",
    portalAccessStatus: "active",
    assignedClientIds: [companyId],
    assignedProjectIds: [],
    assignedTaskIds: [],
    assignedDocumentIds: [],
    createdAt: now,
    updatedAt: now,
  };
}

function normalizeCompanyUserRole(role: CompanyUserRoleValue | string): CompanyUserRoleValue {
  switch (role) {
    case "manager":
    case "company_manager":
      return "company_manager";
    case "supervisor":
    case "company_supervisor":
      return "company_supervisor";
    case "team_member":
    case "company_team_member":
      return "company_team_member";
    case "admin":
    case "company_admin":
      return "company_admin";
    case "viewer":
      return "viewer";
    case "customer_client_user":
      return "customer_client_user";
    default:
      return "company_team_member";
  }
}

function getOnboardingJobTitle(role: CompanyUserRoleValue) {
  return {
    company_admin: "AR Administrator",
    company_manager: "AR Manager",
    company_supervisor: "AR Supervisor",
    company_team_member: "AR Collector",
    customer_client_user: "Client User",
    viewer: "Read Only Reviewer",
  }[role];
}

function splitFullName(fullName: string) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

function ensureUniqueCompanySlug(baseSlug: string, companies: BusinessCenterCompanyRecord[]) {
  const existing = new Set(companies.map((company) => company.companySlug));
  if (!existing.has(baseSlug)) return baseSlug;

  let suffix = 2;
  let nextSlug = `${baseSlug}-${suffix}`;
  while (existing.has(nextSlug)) {
    suffix += 1;
    nextSlug = `${baseSlug}-${suffix}`;
  }
  return nextSlug;
}
